import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Container from '../common/Container';
import Button from '../common/Button';
import Footer from '../layout/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBuilding, faEnvelope, faLock, faUser, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../hooks/useTheme';
import { signIn, signUp, confirmSignUp, getCurrentUser } from '../../services/authService';
import { getSubscriptionChoice } from '../../utils/organizationUtils';
import { isRegistrationOpen } from '../../config/launch';

const GetStartedWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const GetStartedContent = styled.div`
  flex: 1;
  padding: 6rem 0;
  background: var(--background-primary);
`;

const FormContainer = styled.div`
  max-width: 650px;
  margin: 0 auto;
  padding: 2.5rem;
  background: var(--card-background);
  border-radius: 12px;
  box-shadow: var(--card-shadow);
  transition: all 0.3s ease;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 2rem;
  color: var(--text-primary);
  font-size: 1.75rem;
  font-weight: 700;

  @media (max-width: 576px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  margin-bottom: 2rem;
  color: var(--text-secondary);
  font-size: 1.1rem;

  @media (max-width: 576px) {
    font-size: 1rem;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: var(--text-primary);

  @media (prefers-color-scheme: dark) {
    color: #f5f5f5;
  }
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
  box-sizing: border-box;
  width: 100%;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--background-primary);
  color: var(--text-primary);
  font-size: 1rem;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(var(--accent-primary-rgb), 0.2);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;


const ErrorMessage = styled.div`
  color: #e53935;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;


const SuccessMessage = styled.div`
  text-align: center;
  color: #43a047;
  margin-bottom: 1.5rem;
`;



const GetStarted = () => {
  // Authentication states
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  // UI states
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'register' ? 'register' : 'login';
  const [authMode, setAuthMode] = useState(initialMode); // 'login', 'register', 'verify'
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [authError, setAuthError] = useState('');

  const navigate = useNavigate();

  // Use the theme hook to ensure theme persistence
  const { theme } = useTheme();

  // Check if user is already authenticated and scroll to top
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await getCurrentUser();
        if (user) {
          // User is already authenticated, redirect to subscription management
          navigate('/manage-subscription');
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
      }
    };

    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    checkAuth();
  }, [navigate]);

  const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const validateForm = () => {
    // Reset errors state
    setErrors({});
    setAuthError('');

    const newErrors = {};

    // Validate email
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Validate password for login and register
    if (authMode !== 'verify') {
      if (!password) {
        newErrors.password = 'Password is required';
      } else if (password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
    }

    // Additional validation for register mode
    if (authMode === 'register') {
      if (!name.trim()) {
        newErrors.name = 'Name is required';
      }
      if (!confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    // Validation for verification code
    if (authMode === 'verify') {
      if (!verificationCode) {
        newErrors.verificationCode = 'Verification code is required';
      }
    }

    // If there are errors, set them and return false
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    return true;
  };

  const handleLogin = async () => {
    try {
      setIsSubmitting(true);

      const result = await signIn(email, password);

      if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        // Handle new password required challenge if needed
        setAuthError('Your account requires a password reset. Please contact support.');
      } else {
        // Successful login, redirect to subscription management
        navigate('/manage-subscription');
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthError(error.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async () => {
    try {
      setIsSubmitting(true);

      const result = await signUp(email, password, { name });

      // Store the username for later use
      if (result && result.username) {
        localStorage.setItem('tempUsername', result.username);
      }

      // Move to verification step
      setAuthMode('verify');
      setIsSuccess(true);
    } catch (error) {
      console.error('Registration error:', error);

      if (error.code === 'UsernameExistsException') {
        setAuthError('An account with this email already exists. Please sign in instead.');
      } else {
        setAuthError(error.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async () => {
    try {
      setIsSubmitting(true);

      await confirmSignUp(email, verificationCode);

      // After verification, automatically sign in
      try {
        await signIn(email, password);
        // Clear the temporary username after successful sign-in
        localStorage.removeItem('tempUsername');
        navigate('/manage-subscription');
      } catch (signInError) {
        console.error('Auto sign-in after verification failed:', signInError);
        // If auto sign-in fails, just show success and let user sign in manually
        setAuthError('Verification successful! Please sign in with your credentials.');
        setAuthMode('login');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setAuthError(error.message || 'Failed to verify account. Please check the code and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (authMode === 'login') {
      handleLogin();
    } else if (authMode === 'register') {
      handleRegister();
    } else if (authMode === 'verify') {
      handleVerify();
    }
  };

  const switchAuthMode = (mode) => {
    setAuthMode(mode);
    setErrors({});
    setAuthError('');
  };

  // Pre-launch gate: until registration opens, show an "almost launching" screen instead
  // of the sign-up / checkout form, so no one creates an account or pays before we're ready.
  if (!isRegistrationOpen()) {
    return (
      <GetStartedWrapper>
        <GetStartedContent>
          <Container>
            <FormContainer style={{ textAlign: 'center', maxWidth: 560 }}>
              <div style={{ width: '90px', margin: '0 auto 1.5rem' }}>
                <img src="/signage-logo.png" alt="Signage Logo" style={{ width: '100%', height: 'auto' }} />
              </div>
              <Title style={{ marginBottom: '1rem' }}>We're almost ready to launch 🚀</Title>
              <Subtitle style={{ marginBottom: '2rem' }}>
                Sivo is putting on the finishing touches. Sign-ups will open very soon —
                thanks for your patience, and check back shortly!
              </Subtitle>
              <Button variant="primary" onClick={() => navigate('/')} fullWidth>
                Back to Home
              </Button>
              <p style={{
                textAlign: 'center', marginTop: '1.5rem', fontSize: '0.875rem',
                color: theme === 'dark' ? '#b0b0b0' : 'var(--text-secondary)'
              }}>
                Questions? Contact us at support@signage-llc.com
              </p>
            </FormContainer>
          </Container>
        </GetStartedContent>
        <Footer />
      </GetStartedWrapper>
    );
  }

  return (
    <GetStartedWrapper>
      <GetStartedContent>
        <Container>
          <FormContainer>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <Button
                variant="secondary"
                onClick={() => navigate(-1)}
                style={{ padding: '8px 16px' }}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </Button>
              <div style={{ width: '80px' }}><img src="/signage-logo.png" alt="Signage Logo" style={{ width: '100%', height: 'auto' }} /></div>
            </div>

            <Title>
              {authMode === 'login' ? 'Sign In to Your Account' : 
               authMode === 'register' ? 'Create Your Account' : 
               'Verify Your Email'}
            </Title>

            {authError && (
              <div style={{
                marginBottom: '1.5rem',
                padding: '0.75rem',
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '0.875rem',
                backgroundColor: 'rgba(229, 57, 53, 0.1)',
                color: '#e53935'
              }}>
                {authError}
              </div>
            )}

            {authMode === 'verify' ? (
              <form onSubmit={handleSubmit}>
                <Subtitle>
                  We've sent a verification code to your email address. Please enter it below to verify your account.
                </Subtitle>

                <InputGroup>
                  <Label htmlFor="verificationCode">Verification Code</Label>
                  <InputIcon>
                    <FontAwesomeIcon icon={faLock} />
                  </InputIcon>
                  <Input
                    type="text"
                    id="verificationCode"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="Enter verification code"
                  />
                  {errors.verificationCode && <ErrorMessage>{errors.verificationCode}</ErrorMessage>}
                </InputGroup>

                <Button
                  variant="primary"
                  type="submit"
                  fullWidth
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Verifying...' : 'Verify Account'}
                </Button>

                <p style={{ 
                  textAlign: 'center', 
                  marginTop: '1.5rem', 
                  fontSize: '0.875rem', 
                  color: theme === 'dark' ? '#b0b0b0' : 'var(--text-secondary)'
                }}>
                  Didn't receive a code?{' '}
                  <Link to="#" onClick={() => switchAuthMode('register')} style={{ color: theme === 'dark' ? '#4fc3f7' : 'inherit' }}>
                    Try again
                  </Link>
                </p>
              </form>
            ) : (
              <form onSubmit={handleSubmit}>

                {authMode === 'register' && (
                  <InputGroup>
                    <Label htmlFor="name">Full Name</Label>
                    <InputIcon>
                      <FontAwesomeIcon icon={faUser} />
                    </InputIcon>
                    <Input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your name"
                    />
                    {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                  </InputGroup>
                )}

                <InputGroup>
                  <Label htmlFor="email">Email Address</Label>
                  <InputIcon>
                    <FontAwesomeIcon icon={faEnvelope} />
                  </InputIcon>
                  <Input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                  />
                  {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                </InputGroup>

                <InputGroup>
                  <Label htmlFor="password">Password</Label>
                  <InputIcon>
                    <FontAwesomeIcon icon={faLock} />
                  </InputIcon>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <Input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                    />
                    <div 
                      style={{ 
                        position: 'absolute', 
                        right: '12px', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        color: 'var(--text-secondary)'
                      }}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </div>
                  </div>
                  {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
                </InputGroup>

                {authMode === 'register' && (
                  <InputGroup>
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <InputIcon>
                      <FontAwesomeIcon icon={faLock} />
                    </InputIcon>
                    <div style={{ position: 'relative', width: '100%' }}>
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm password"
                      />
                      <div 
                        style={{ 
                          position: 'absolute', 
                          right: '12px', 
                          top: '50%', 
                          transform: 'translateY(-50%)',
                          cursor: 'pointer',
                          color: 'var(--text-secondary)'
                        }}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                      </div>
                    </div>
                    {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword}</ErrorMessage>}
                  </InputGroup>
                )}

                <Button
                  variant="primary"
                  type="submit"
                  fullWidth
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : 
                   authMode === 'login' ? 'Sign In' : 'Create Account'}
                </Button>

                <p style={{ 
                  textAlign: 'center', 
                  marginTop: '1.5rem', 
                  fontSize: '0.875rem', 
                  color: theme === 'dark' ? '#b0b0b0' : 'var(--text-secondary)'
                }}>
                  {authMode === 'login' ? (
                    <>
                      Don't have an account?{' '}
                      <Link to="#" onClick={() => switchAuthMode('register')} style={{ color: theme === 'dark' ? '#4fc3f7' : 'inherit' }}>
                        Sign Up
                      </Link>
                    </>
                  ) : (
                    <>
                      Already have an account?{' '}
                      <Link to="#" onClick={() => switchAuthMode('login')} style={{ color: theme === 'dark' ? '#4fc3f7' : 'inherit' }}>
                        Sign In
                      </Link>
                    </>
                  )}
                </p>

                <p style={{ 
                  textAlign: 'center', 
                  marginTop: '1rem', 
                  fontSize: '0.875rem', 
                  color: theme === 'dark' ? '#b0b0b0' : 'var(--text-secondary)'
                }}>
                  By subscribing, you agree to our{' '}
                  <Link to="/terms-of-service" style={{ color: theme === 'dark' ? '#4fc3f7' : 'inherit' }}>Terms of Service</Link>,{' '}
                  <Link to="/privacy-policy" style={{ color: theme === 'dark' ? '#4fc3f7' : 'inherit' }}>Privacy Policy</Link>,{' '}
                  <Link to="/software-license-agreement" style={{ color: theme === 'dark' ? '#4fc3f7' : 'inherit' }}>Software Agreement</Link>, and{' '}
                  <Link to="/refund-policy" style={{ color: theme === 'dark' ? '#4fc3f7' : 'inherit' }}>Refund Policy</Link>.
                </p>
              </form>
            )}
          </FormContainer>
        </Container>
      </GetStartedContent>
      <Footer />
    </GetStartedWrapper>
  );
};

export default GetStarted;
